<template>
  <div class="container">
    <div class="table-wrapper">
      <Table
        class="table-housemate-data"
        :columns="columns"
        :data-source="housemateData"
        :pagination="pagination"
        @change="handleTableChange"
      >
        <template slot="operation" slot-scope="text, record, index">
          <div class="row-actions">
            <button
              class="housemates edit"
              @click="showEditPopupAction(true, record.rental)"
            >
              <img src="../../assets/rentalButtonIcons/edit.svg" />
            </button>
            <button
              class="housemates delete"
              @click="showDeletePopupAction(true, record.uuid)"
            >
              <img src="../../assets/rentalButtonIcons/delete.svg" />
            </button>
          </div>
        </template>
      </Table>
    </div>
    <section
      v-if="!noRentals && housemateData.length === 0"
      class="empty-contact-screen"
    >
      <img src="../../assets/no_filtered_housemates.svg" />
      <div style="font-weight: 900">No results found</div>
      <div>We couldn’t find the housemate you are looking for</div>
    </section>
    <div v-if="showEditPopup">
      <NewHouseMatePopup
        @submitted="reload()"
        :selectedRental="edit_rental"
        @cancel="showEditPopupAction(false, null)"
        @loading="$emit('loading', $event)"
      />
    </div>
    <div v-if="showDeletePopup">
      <delete-popup
        @confirm="erase"
        @cancel="showDeletePopupAction(false, null)"
        :thingName="'Housemate'"
      />
    </div>
    <section
      v-if="
        noRentals && searchInput.length === 0 && filteredProperties.length === 0
      "
      class="empty-contact-screen"
    >
      <img src="../../assets/empty_rental_icon.svg" />
      <div>Add your first housemate</div>
      <button @click="createRental">New Rental</button>
    </section>
  </div>
</template>

<script>
import moment from 'moment'
import calls from '../../api/calls'
import NewHouseMatePopup from '@/components/contacts/NewHousematePopup'
import DeletePopup from '../../components/GenericDeletePopup.vue'
import { Table } from 'ant-design-vue'
import rentalApi from '../../api/rental'

export default {
  name: 'HousematesList',
  props: {
    filteredProperties: Array,
    searchInput: String,
    tabSelected: String
  },
  components: {
    NewHouseMatePopup,
    DeletePopup,
    Table
  },
  data() {
    return {
      selected_property: null,
      all_rentals: [],
      edit_rental: null,
      showEditPopup: false,
      showDeletePopup: false,
      housemateID: null,
      columns: [
        {
          title: 'Name',
          dataIndex: 'name',
          key: 'name',
          width: 200
        },
        {
          title: 'Number',
          dataIndex: 'number',
          key: 'number',
          width: 150
        },
        {
          title: 'Arrival',
          dataIndex: 'arrival',
          key: 'arrival',
          sorter: (a, b) => a.arrival.length - b.arrival.length,
          width: 130
        },
        {
          title: 'Departure',
          dataIndex: 'departure',
          key: 'departure',
          sorter: (a, b) => a.departure.length - b.departure.length,
          width: 130
        },
        {
          title: 'Room',
          dataIndex: 'room',
          key: 'room',
          width: 80
        },
        {
          title: 'Property',
          dataIndex: 'property',
          key: 'property',
          width: 130
        },
        {
          title: 'Action',
          dataIndex: 'action',
          key: 'action',
          width: 100,
          scopedSlots: { customRender: 'operation' }
        }
      ],
      pagination: {
        current: 1,
        pageSize: 10,
        total: 1
      },
      sort: {
        column: '',
        order: ''
      },
      housemateData: []
    }
  },
  computed: {
    noRentals() {
      return (
        this.housemateData == null ||
        this.housemateData == undefined ||
        this.housemateData.length < 1
      )
    }
  },
  asyncComputed: {
    async rentals() {
      if (
        this.filteredProperties == null ||
        this.filteredProperties == undefined ||
        this.filteredProperties.length < 1
      ) {
        return this.all_rentals
      } else {
        const promises = []
        for (const property of this.filteredProperties) {
          promises.push(calls.GET('properties', `rooms/${property}`))
        }
        const results = await Promise.all(promises)
        let rooms = []
        for (const result of results) {
          const { error, message, data } = result
          rooms.push(...data)
        }
        const ids = rooms.map((e) => e.uuid)

        const filtered = []
        for (let rental of this.all_rentals) {
          if (ids.includes(rental[1].uuid)) {
            filtered.push(rental)
          }
        }
        return filtered
      }
    }
  },
  methods: {
    resetParams() {
      this.pagination.current = 1
      this.pagination.pageSize = 10
      this.pagination.total = 1
    },
    handleTabSelected(timeValue) {
      this.resetParams()
      this.getTableData(timeValue)
    },
    handleTableChange(pagination, filters, { columnKey, order }) {
      this.sortTable(columnKey, order)
      this.pagination = { ...pagination }
      this.getTableData(this.tabSelected)
    },
    sortTable(columnKey, order) {
      if (!order) {
        this.sort.column = ''
        this.sort.order = ''
      } else {
        this.sort.column = columnKey
        this.sort.order = order === 'ascend' ? 'asc' : 'desc'
      }
    },
    async getTableData(timeValue) {
      const page = this.pagination.current
      const pageSize = this.pagination.pageSize
      let time = 'active'
      if (timeValue === 'Future') {
        time = 'upcoming'
      } else if (timeValue === 'Past') {
        time = 'past'
      }

      const rentals = await rentalApi.getHousemates({
        page,
        perPage: pageSize,
        searchValue: this.searchInput,
        sort: this.sort.column,
        sortBy: this.sort.order,
        time,
        propertyIds:
          this.filteredProperties.length > 0
            ? this.filteredProperties.join(',')
            : ''
      })

      this.housemateData = []
      rentals.data.map((element) => {
        const arrival = element[2].arrival.split('T')[0]
        const departure = element[2].departure.split('T')[0]
        const housemate = {
          uuid: element[3].uuid,
          name: element[3].name,
          number: element[3].phone,
          email: element[3].email,
          arrival: moment
            .utc(arrival)
            .local(true)
            .format('ddd MMM D YYYY')
            .toString(),
          departure: moment
            .utc(departure)
            .local(true)
            .format('ddd MMM D YYYY')
            .toString(),
          room: element[1].name,
          property: element[0].name,
          rental: element
        }
        this.housemateData.push(housemate)
      })
      this.pagination.total = rentals.total_all_data
    },
    async erase() {
      const { message, data } = await calls.DELETE(
        'housemates',
        `${this.housemateID}`
      )
      await this.reload()
    },
    createRental() {
      this.$emit('create')
    },
    deepCopy(arrayOfObjs) {
      //crea una copia de un arreglo de objetos
      const copy = []
      for (let obj of arrayOfObjs) {
        const newObj = { ...obj }
        copy.push(newObj)
      }
      return copy
    },
    doubleDeepCopy(arrayOfArrayOfObjs) {
      //crea una copia de un arreglo de arreglo de objetos
      const copy = []
      for (let array of arrayOfArrayOfObjs) {
        copy.push(this.deepCopy(array))
      }
      return copy
    },
    async showEditPopupAction(value, rental) {
      this.edit_rental = rental
      this.showEditPopup = value
    },
    async showDeletePopupAction(value, housemateID) {
      this.showDeletePopup = value
      this.housemateID = housemateID
    },
    async reload() {
      await this.loadRentals()
      this.showEditPopup = false
      this.showDeletePopup = false
      this.selected_property = null
      this.$nextTick(() => {
        this.selected_property = this.selProperty
      })
      this.$asyncComputed.rentals.update()
    },
    async loadRentals() {
      //load and parse data
      const rentals = await calls.GET('rental/in/full')
      rentals.data.map((rental) => {
        // For sorting
        rental[2].arrivalOrig = rental[2].arrival
        rental[2].departureOrig = rental[2].departure

        const arrival = rental[2].arrival.split('T')[0]
        const departure = rental[2].departure.split('T')[0]
        rental[2].arrival = moment.utc(arrival)
        rental[2].departure = moment.utc(departure)
        rental[2].status = 'Past'
        if (rental[2].arrival.isAfter(moment())) {
          rental[2].status = 'Future'
        } else if (rental[2].departure.isAfter(moment())) {
          rental[2].status = 'Present'
        }
      })

      this.all_rentals = rentals.data
    }
  },
  async beforeMount() {
    await this.getTableData()
  },
  watch: {
    tabSelected: {
      handler(value) {
        this.handleTabSelected(value)
      }
    },
    searchInput() {
      this.resetParams()
      this.getTableData()
    },
    filteredProperties() {
      this.resetParams()
      this.getTableData()
    }
  }
}
</script>

<style scoped>
* {
  margin: 0;
  padding: 0;
}

.container {
  display: flex;
  flex-direction: column;
  background-color: white;
  flex: 1;
  height: calc(85% - 24px);
}

section.item-list {
  overflow: scroll;
}

.item {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  padding: 1%;
  height: 48px;
  min-height: 48px;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0px;
  text-align: left;
  color: #192a3e;

  border-bottom: rgb(0, 0, 0, 0.1) 1px solid;
  background-color: white;
}

.housemates {
  flex-basis: 14%;
  padding-left: 0.5%;
}

.housemates.edit {
  flex-basis: 3%;
  border: none;
  background-color: transparent;
}

.housemates.delete {
  flex-basis: 3%;
  border: none;
  background-color: transparent;
}

.housemates.edit:hover,
.housemates.delete:hover {
  cursor: pointer;
}

.empty-contact-screen {
  width: 100%;
  height: 70%;
  padding-top: 5%;
}

.empty-contact-screen img {
  width: 25%;
  height: 25%;
}

.empty-contact-screen div {
  font-family: Montserrat;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 40px;
}

.empty-contact-screen button {
  background: #66b015;
  border-radius: 4px;
  border: none;
  color: white;
  font-family: Montserrat;
  font-size: 16px;
  height: 40px;
  width: 192px;
}

table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

th,
td {
  padding: 10px;
  text-align: left;
  border-bottom: 1px solid #ddd;
  word-wrap: break-word;
}

th {
  color: var(--rentalios-dark);
  border: none;
  font-weight: bold;
  background-color: #ebf0e6;
  cursor: pointer;
}

.sort-arrows {
  width: 10px;
  margin-left: 5px;
}

.row-actions {
  display: flex;
  align-items: center;
  justify-content: space-around;
}

.table-housemate-data ::v-deep th {
  background-color: #ebf0e6;
  color: var(--rentalios-dark);
  font-weight: 700;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .container {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .table-wrapper {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    min-width: 800px; /* Ensure table doesn't get too compressed */
  }

  .table-housemate-data {
    font-size: 14px;
  }

  .table-housemate-data ::v-deep th,
  .table-housemate-data ::v-deep td {
    padding: 8px 6px;
    font-size: 13px;
    white-space: nowrap;
  }

  .table-housemate-data ::v-deep th {
    font-size: 12px;
    font-weight: 600;
  }

  .row-actions {
    gap: 8px;
  }

  .housemates.edit,
  .housemates.delete {
    padding: 4px;
    min-width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .housemates.edit img,
  .housemates.delete img {
    width: 16px;
    height: 16px;
  }

  .empty-contact-screen {
    height: auto;
    padding: 40px 20px;
    text-align: center;
  }

  .empty-contact-screen img {
    width: 40%;
    height: auto;
    margin-bottom: 16px;
  }

  .empty-contact-screen div {
    font-size: 14px;
    line-height: 20px;
    margin-bottom: 8px;
  }

  .empty-contact-screen button {
    width: 100%;
    max-width: 200px;
    height: 44px;
    font-size: 16px;
  }
}

@media (max-width: 480px) {
  .table-wrapper {
    min-width: 700px;
  }

  .table-housemate-data ::v-deep th,
  .table-housemate-data ::v-deep td {
    padding: 6px 4px;
    font-size: 12px;
  }

  .table-housemate-data ::v-deep th {
    font-size: 11px;
  }

  .housemates.edit,
  .housemates.delete {
    min-width: 28px;
    height: 28px;
  }

  .housemates.edit img,
  .housemates.delete img {
    width: 14px;
    height: 14px;
  }
}
</style>
